import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { BbBannedUser, bbGetBannedUsers, bbLbBanUser } from '../../../services/blindBoxService';
import { BanUserDialog, UnbanConfirmDialog } from './BanDialogs';

const fmtTime = (v?: string | number | null) =>
  v !== undefined && v !== null && v !== '' ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—';

export function SectionBannedUsers({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<BbBannedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [unbanTarget, setUnbanTarget] = useState<BbBannedUser | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    bbGetBannedUsers({ page: page + 1, pageSize, activeOnly })
      .then((r) => {
        setRows(r.items || []);
        setTotal(r.total || 0);
      })
      .catch((e: any) => notify(e?.message || 'Error', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [page, pageSize, activeOnly, notify]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const ban = async (body: Parameters<typeof bbLbBanUser>[0]) => {
    try {
      await bbLbBanUser(body);
      notify(t('module.blindBox.banOk'), { type: 'success' });
      setBanOpen(false);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      throw e;
    }
  };

  const unban = async () => {
    if (!unbanTarget) return;
    try {
      await bbLbBanUser({ userId: unbanTarget.userId, banned: false });
      notify(t('module.blindBox.lbUnbanned'), { type: 'success' });
      setUnbanTarget(null);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      throw e;
    }
  };

  return (
    <Box>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {t('module.blindBox.banHint')}
      </Alert>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={t('module.blindBox.banActiveOnly')}
        />
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>
          {t('module.blindBox.refresh')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <PermissionButton resource="bb-leaderboard" action="update">
          <Button variant="contained" color="error" onClick={() => setBanOpen(true)}>
            {t('module.blindBox.lbBan')}
          </Button>
        </PermissionButton>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.blindBox.banColUserId')}</TableCell>
              <TableCell>{t('module.blindBox.banColStatus')}</TableCell>
              <TableCell>{t('module.blindBox.banReason')}</TableCell>
              <TableCell>{t('module.blindBox.banColBannedBy')}</TableCell>
              <TableCell>{t('module.blindBox.banColBannedAt')}</TableCell>
              <TableCell>{t('module.blindBox.banColUnbannedBy')}</TableCell>
              <TableCell>{t('module.blindBox.banColUnbannedAt')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.blindBox.banEmpty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.userId} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.userId}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={r.isBanned ? 'error' : 'default'}
                      label={t(
                        r.isBanned
                          ? 'module.blindBox.lbBannedTag'
                          : 'module.blindBox.banUnbannedTag'
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={r.reason || ''} arrow>
                      <Typography variant="body2" noWrap>
                        {r.reason || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{r.bannedBy || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(r.bannedAt)}</TableCell>
                  <TableCell>{r.unbannedBy || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(r.unbannedAt)}</TableCell>
                  <TableCell align="right">
                    {r.isBanned ? (
                      <PermissionButton resource="bb-leaderboard" action="update">
                        <Button size="small" variant="outlined" onClick={() => setUnbanTarget(r)}>
                          {t('module.blindBox.lbUnban')}
                        </Button>
                      </PermissionButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_e, next) => setPage(next)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setPageSize(Number.isFinite(next) && next > 0 ? next : 20);
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />

      <BanUserDialog open={banOpen} onClose={() => setBanOpen(false)} onConfirm={ban} />
      <UnbanConfirmDialog
        open={!!unbanTarget}
        userLabel={unbanTarget?.userId || ''}
        onClose={() => setUnbanTarget(null)}
        onConfirm={unban}
      />
    </Box>
  );
}
